import React, { useState, useEffect, useRef } from 'react';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction
} from '../../components/ui/alert-dialog';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { TabsContent } from '../../components/ui/tabs';
import { Plus, Trash2, Edit, Printer, Download, AlertCircle, Info, Loader2, ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { QRPreviewModal } from '../shared/WizardComponents';
import { SFScheduleDetailsStep, hasSFLiteErrors } from '../../components/SFScheduleDetailsStep';
import { getSFTypeImageUrl } from '../../components/TypeImageMarkingSection';
import { API } from '../../App';

// Normalized snapshot used for unsaved-changes detection: numeric strings and numbers
// compare equal, so reverting an edit clears the dirty state.
const toComparableNumber = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? String(value) : parsed;
};

const snapshotForm = (form = {}) => JSON.stringify({
  label: form.label || '',
  storefrontTypeId: form.storefrontTypeId || '',
  location: form.location || '',
  width_in: toComparableNumber(form.width_in),
  height_in: toComparableNumber(form.height_in),
  roWidth_in: toComparableNumber(form.roWidth_in),
  roHeight_in: toComparableNumber(form.roHeight_in),
  lites: (form.lites || []).map(lite => ({
    liteType: lite.liteType || 'glass',
    width_in: toComparableNumber(lite.width_in),
    height_in: toComparableNumber(lite.height_in),
    glassTypeId: lite.glassTypeId || '',
    metalPanelTypeId: lite.metalPanelTypeId || ''
  })),
  liteMarks: (form.liteMarks || []).map(mark => ({
    liteId: mark.liteId,
    x: Math.round(Number(mark.x) * 100) / 100,
    y: Math.round(Number(mark.y) * 100) / 100
  }))
});

export const SFPanelListSection = ({
  // Data
  storefrontPanelList,
  storefrontTypes,
  storefrontGlassTypes,
  storefrontMetalPanelTypes,
  storefrontElevations,
  selectedSFPanelListIds,
  newSFPanel,
  editingSFPanel,
  showSFPanelDialog,
  uploadingSFPanelExcel,
  showSFPanelExcelDialog,
  sfPanelExcelPreview,
  sfScheduleSearch,
  sfScheduleFilters,
  sfScheduleSortBy,
  operationLoading,
  // Permissions
  canManageProject,
  // Handlers
  setNewSFPanel,
  setShowSFPanelDialog,
  setShowSFPanelExcelDialog,
  setSFPanelExcelPreview,
  setSfScheduleSearch,
  setSfScheduleFilters,
  setSfScheduleSortBy,
  selectAllItems,
  confirmBulkDelete,
  toggleItemSelection,
  handleCreateSFPanel,
  handleEditSFPanel,
  handleDeleteSFPanel,
  handleSFPanelExcelUpload,
  handlePrintAllSFPanelLabels,
  handlePrintSFPanelLabel,
  handlePrintSFPanelQR,
  handleUpdateSFPanelPreviewRow,
  handleSFPanelBulkImport,
}) => {
  const [qrModal, setQrModal] = useState({ open: false, qrToken: null, label: '' });
  const [step, setStep] = useState(1);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [stepOneErrors, setStepOneErrors] = useState({});
  const [liteTouched, setLiteTouched] = useState({});
  const initialSnapshot = useRef(null);
  const latestForm = useRef(newSFPanel);
  latestForm.current = newSFPanel;

  const sessionKey = showSFPanelDialog ? (editingSFPanel?.id || 'new') : null;

  useEffect(() => {
    if (!showSFPanelDialog) {
      initialSnapshot.current = null;
      setShowDiscardConfirm(false);
      return;
    }
    // Snapshot the initial values once per modal session
    initialSnapshot.current = snapshotForm(latestForm.current);
    setShowDiscardConfirm(false);
  }, [showSFPanelDialog, sessionKey]);

  const isDirty = initialSnapshot.current !== null && snapshotForm(newSFPanel) !== initialSnapshot.current;

  const handleWizardOpenChange = (open) => {
    if (!open) {
      setStep(1);
      setStepOneErrors({});
      setLiteTouched({});
    }
    setShowSFPanelDialog(open);
  };

  // Dismissal paths: Cancel, X, outside click, Escape
  const attemptClose = () => {
    if (operationLoading) return;
    if (isDirty) {
      setShowDiscardConfirm(true);
      return;
    }
    handleWizardOpenChange(false);
  };

  const confirmDiscard = () => {
    setShowDiscardConfirm(false);
    handleWizardOpenChange(false);
  };

  const selectedSFType = storefrontTypes.find(t => t.id === newSFPanel.storefrontTypeId) || null;
  const selectedSFTypeImageUrl = getSFTypeImageUrl(selectedSFType, API);

  // Changing the SF Type changes the image, so existing marks cannot carry over
  const handleStorefrontTypeChange = (value) => {
    setStepOneErrors(prev => ({ ...prev, storefrontTypeId: undefined }));
    if (value === newSFPanel.storefrontTypeId) return;
    const existingMarks = newSFPanel.liteMarks || [];
    if (existingMarks.length > 0 && !window.confirm(
      `This schedule has ${existingMarks.length} mark${existingMarks.length === 1 ? '' : 's'} on the current SF Type image. Changing the Storefront Type discards ${existingMarks.length === 1 ? 'it' : 'them'}. Continue?`
    )) {
      return;
    }
    setNewSFPanel({ ...newSFPanel, storefrontTypeId: value, liteMarks: [], liteMarksTypeId: value });
  };

  const validateStepOne = () => {
    const errors = {};
    if (!newSFPanel.label || !newSFPanel.label.trim()) errors.label = 'This field is required';
    if (!newSFPanel.storefrontTypeId) errors.storefrontTypeId = 'This field is required';
    setStepOneErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validateStepOne()) setStep(2);
  };

  const handleFinalSubmit = (e) => {
    if (!validateStepOne()) {
      setStep(1);
      return;
    }
    const lites = newSFPanel.lites || [];
    if (lites.length > 0 && hasSFLiteErrors(lites)) {
      const allTouched = {};
      lites.forEach((_, index) => {
        allTouched[index] = { width_in: true, height_in: true, liteType: true, glassTypeId: true, metalPanelTypeId: true };
      });
      setLiteTouched(allTouched);
      setStep(2);
      return;
    }
    handleCreateSFPanel(e);
  };

  // Enter key / submit while on step 1 advances instead of creating the schedule
  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (step === 1) {
      handleNext();
      return;
    }
    handleFinalSubmit(e);
  };

  return (
    <TabsContent value="sf-panel-list">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Schedules</h2>
        <div className="flex gap-2">
          {canManageProject && storefrontTypes.length === 0 && (
            <div className="text-sm text-slate-500 flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              Create a Storefront Type first to add schedules
            </div>
          )}
          {canManageProject && storefrontTypes.length > 0 && (
            <>
              {storefrontPanelList.length > 0 && (
                <>
                  <Button variant="outline" onClick={() => selectAllItems('sfPanelList')}>
                    {selectedSFPanelListIds.length === storefrontPanelList.length ? 'Deselect All' : 'Select All'}
                  </Button>
                  <Button 
                    onClick={() => confirmBulkDelete('sfPanelList')}
                    disabled={selectedSFPanelListIds.length === 0}
                    className="bg-red-600 hover:bg-red-700 text-white disabled:bg-red-300"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Selected ({selectedSFPanelListIds.length})
                  </Button>
                </>
              )}
              <Button 
                variant="outline"
                onClick={handlePrintAllSFPanelLabels}
                disabled={storefrontPanelList.length === 0}
              >
                <Printer className="h-4 w-4 mr-2" />
                Print All QR Labels
              </Button>
              <Button 
                variant="outline"
                onClick={() => document.getElementById('sf-panel-excel-upload').click()}
                disabled={uploadingSFPanelExcel}
              >
                <Download className="h-4 w-4 mr-2" />
                {uploadingSFPanelExcel ? 'Uploading...' : 'Import from Excel'}
              </Button>
              <input
                id="sf-panel-excel-upload"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleSFPanelExcelUpload}
                style={{ display: 'none' }}
              />
              <Dialog
                open={showSFPanelDialog}
                onOpenChange={(nextOpen) => {
                  if (!nextOpen) { attemptClose(); return; }
                  handleWizardOpenChange(true);
                }}
              >
                <DialogTrigger asChild>
                  <Button data-testid="add-sf-schedule-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Schedule
                  </Button>
                </DialogTrigger>
            <DialogContent
              className="max-w-4xl max-h-[90vh] overflow-y-auto"
              onInteractOutside={(e) => { if (isDirty || operationLoading) { e.preventDefault(); attemptClose(); } }}
              onEscapeKeyDown={(e) => { if (isDirty || operationLoading) { e.preventDefault(); attemptClose(); } }}
            >
              <DialogHeader>
                <DialogTitle>{editingSFPanel ? 'Edit Schedule' : 'New Schedule'}</DialogTitle>
                <DialogDescription>
                  {step === 1
                    ? 'Step 1 of 2 - Schedule information'
                    : 'Step 2 of 2 - Dimensions and lites'}
                </DialogDescription>
              </DialogHeader>

              {/* Step indicator */}
              <div className="flex items-center gap-3 mt-2" data-testid="sf-wizard-step-indicator">
                {[
                  { number: 1, title: 'Schedule Info' },
                  { number: 2, title: 'Details & Lites' },
                ].map((s, index) => (
                  <React.Fragment key={s.number}>
                    <div className="flex items-center gap-2">
                      <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold ${
                        step === s.number
                          ? 'bg-[#FF6600] text-white'
                          : step > s.number
                            ? 'bg-green-100 text-green-700'
                            : 'bg-slate-100 text-slate-500'
                      }`}>
                        {step > s.number ? <Check className="h-3.5 w-3.5" /> : s.number}
                      </div>
                      <span className={`text-sm ${step === s.number ? 'font-semibold text-slate-900' : 'text-slate-500'}`}>
                        {s.title}
                      </span>
                    </div>
                    {index === 0 && <div className="flex-1 h-px bg-slate-200" />}
                  </React.Fragment>
                ))}
              </div>

              <form onSubmit={handleFormSubmit} className="space-y-4 mt-4">
                {step === 1 ? (
                  <>
                    <div className="space-y-2">
                      <Label>Label *</Label>
                      <Input
                        data-testid="sf-panel-label-input"
                        value={newSFPanel.label}
                        onChange={(e) => {
                          setStepOneErrors(prev => ({ ...prev, label: undefined }));
                          setNewSFPanel({ ...newSFPanel, label: e.target.value });
                        }}
                        placeholder="e.g., SF-001"
                        className={stepOneErrors.label ? 'border-red-500' : ''}
                      />
                      {stepOneErrors.label && <p className="text-xs text-red-500">{stepOneErrors.label}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label>Storefront Type *</Label>
                      <Select
                        value={newSFPanel.storefrontTypeId}
                        onValueChange={(value) => handleStorefrontTypeChange(value)}
                      >
                        <SelectTrigger data-testid="sf-panel-type-select" className={stepOneErrors.storefrontTypeId ? 'border-red-500' : ''}>
                          <SelectValue placeholder="Select storefront type" />
                        </SelectTrigger>
                        <SelectContent>
                          {storefrontTypes.map((sf) => (
                            <SelectItem key={sf.id} value={sf.id}>{sf.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {stepOneErrors.storefrontTypeId && <p className="text-xs text-red-500">{stepOneErrors.storefrontTypeId}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label>Location</Label>
                      <Input data-testid="sf-panel-location-input" value={newSFPanel.location} onChange={(e) => setNewSFPanel({...newSFPanel, location: e.target.value})} placeholder="e.g., Floor 2, Bay 3" />
                    </div>
                  </>
                ) : (
                  <SFScheduleDetailsStep
                    value={newSFPanel}
                    onChange={setNewSFPanel}
                    storefrontGlassTypes={storefrontGlassTypes}
                    storefrontMetalPanelTypes={storefrontMetalPanelTypes}
                    touched={liteTouched}
                    setTouched={setLiteTouched}
                    sfType={selectedSFType}
                    sfTypeImageUrl={selectedSFTypeImageUrl}
                  />
                )}

                <div className="flex justify-between gap-3 pt-4 border-t">
                  <Button
                    key={step === 1 ? 'cancel' : 'back'}
                    type="button"
                    variant="outline"
                    onClick={() => (step === 1 ? attemptClose() : setStep(1))}
                    disabled={operationLoading}
                    data-testid={step === 1 ? 'sf-wizard-cancel-btn' : 'sf-wizard-back-btn'}
                  >
                    {step === 1 ? 'Cancel' : (<><ArrowLeft className="h-4 w-4 mr-2" />Back</>)}
                  </Button>
                  <div className="flex gap-3">
                    {step === 2 && (
                      <Button key="cancel-step2" type="button" variant="outline" onClick={attemptClose} disabled={operationLoading} data-testid="sf-wizard-cancel-btn">
                        Cancel
                      </Button>
                    )}
                    {step === 1 ? (
                      <Button key="next" type="button" onClick={handleNext} data-testid="sf-wizard-next-btn">
                        Next<ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    ) : (
                      <Button key="submit" type="button" onClick={handleFinalSubmit} data-testid="sf-panel-submit" disabled={operationLoading}>
                        {operationLoading ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            {editingSFPanel ? 'Saving...' : 'Creating...'}
                          </>
                        ) : (
                          editingSFPanel ? 'Save Changes' : 'Create'
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </form>
            </DialogContent>
          </Dialog>
            </>
          )}
        </div>
      </div>

      {storefrontPanelList.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-slate-600">
              {storefrontTypes.length === 0 ? 'Create storefront types first' : 
               (storefrontGlassTypes.length === 0 && storefrontMetalPanelTypes.length === 0) ? 'Create glass or metal types first' :
               'No panels created yet'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Filter Section */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1">
              <Input
                type="text"
                placeholder="Search by label..."
                value={sfScheduleSearch}
                onChange={(e) => setSfScheduleSearch(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Select
                value={sfScheduleFilters.type}
                onValueChange={(value) => setSfScheduleFilters({ ...sfScheduleFilters, type: value === 'all' ? '' : value })}
              >
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filter by Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {storefrontTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={sfScheduleSortBy}
                onValueChange={(value) => setSfScheduleSortBy(value)}
              >
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="createdAt_desc">Newest First</SelectItem>
                  <SelectItem value="createdAt_asc">Oldest First</SelectItem>
                  <SelectItem value="label_asc">Label (A-Z)</SelectItem>
                  <SelectItem value="label_desc">Label (Z-A)</SelectItem>
                  <SelectItem value="type_asc">Type (A-Z)</SelectItem>
                  <SelectItem value="type_desc">Type (Z-A)</SelectItem>
                </SelectContent>
              </Select>
              <Input
                type="text"
                placeholder="Filter by location..."
                value={sfScheduleFilters.location}
                onChange={(e) => setSfScheduleFilters({ ...sfScheduleFilters, location: e.target.value })}
                className="w-48"
              />
              <Button
                variant="outline"
                onClick={() => {
                  setSfScheduleSearch('');
                  setSfScheduleFilters({ type: '', location: '' });
                  setSfScheduleSortBy('createdAt_desc');
                }}
              >
                Clear
              </Button>
            </div>
          </div>
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    {canManageProject && (
                      <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase w-10"></th>
                    )}
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Label</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Dimensions</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Location</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-slate-500 uppercase">QR</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-slate-500 uppercase">
                      <div className="flex items-center justify-center gap-1">
                        Quantity ({storefrontPanelList.reduce((total, panel) => {
                          let panelMarkerCount = 0;
                          storefrontElevations.forEach(elevation => {
                            if (elevation.markers && Array.isArray(elevation.markers)) {
                              panelMarkerCount += elevation.markers.filter(m => m.windowListId === panel.id).length;
                            }
                          });
                          return total + panelMarkerCount;
                        }, 0)})
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-3.5 w-3.5 text-slate-400 hover:text-slate-600 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Type Usage</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </th>
                    {canManageProject && (
                      <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {[...storefrontPanelList]
                    .filter(panel => {
                      if (sfScheduleSearch) {
                        const searchLower = sfScheduleSearch.toLowerCase();
                        if (!panel.label?.toLowerCase().includes(searchLower)) return false;
                      }
                      if (sfScheduleFilters.type && panel.storefrontTypeId !== sfScheduleFilters.type) return false;
                      if (sfScheduleFilters.location) {
                        const locationLower = sfScheduleFilters.location.toLowerCase();
                        if (!panel.location?.toLowerCase().includes(locationLower)) return false;
                      }
                      return true;
                    })
                    .sort((a, b) => {
                    switch (sfScheduleSortBy) {
                      case 'label_asc':
                        return (a.label || '').localeCompare(b.label || '');
                      case 'label_desc':
                        return (b.label || '').localeCompare(a.label || '');
                      case 'type_asc':
                        const typeA = storefrontTypes.find(t => t.id === a.storefrontTypeId)?.name || '';
                        const typeB = storefrontTypes.find(t => t.id === b.storefrontTypeId)?.name || '';
                        return typeA.localeCompare(typeB);
                      case 'type_desc':
                        const typeA2 = storefrontTypes.find(t => t.id === a.storefrontTypeId)?.name || '';
                        const typeB2 = storefrontTypes.find(t => t.id === b.storefrontTypeId)?.name || '';
                        return typeB2.localeCompare(typeA2);
                      case 'createdAt_asc':
                        return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
                      case 'createdAt_desc':
                      default:
                        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
                    }
                  }).map((panel) => {
                    const sfType = storefrontTypes.find(sf => sf.id === panel.storefrontTypeId);
                    // Quantity = marks placed for this schedule on the elevations (calculation unchanged)
                    let markerCount = 0;
                    storefrontElevations.forEach(elevation => {
                      if (elevation.markers && Array.isArray(elevation.markers)) {
                        markerCount += elevation.markers.filter(m => m.windowListId === panel.id).length;
                      }
                    });
                    const isUnused = markerCount === 0;
                    const isSelected = selectedSFPanelListIds.includes(panel.id);

                    return (
                      <tr
                        key={panel.id}
                        className={
                          isSelected
                            ? 'bg-blue-50 hover:bg-blue-100/70'
                            : isUnused
                              ? 'bg-amber-50/40 hover:bg-amber-100/50 dark:bg-amber-500/10'
                              : 'hover:bg-slate-50'
                        }
                        data-testid={`sf-panel-row-${panel.id}`}
                        data-unused={isUnused ? 'true' : 'false'}
                      >
                        {canManageProject && (
                          <td className="px-3 py-2">
                            <Checkbox
                              checked={selectedSFPanelListIds.includes(panel.id)}
                              onCheckedChange={(checked) => toggleItemSelection(panel.id, 'sfPanelList', { target: { checked } })}
                            />
                          </td>
                        )}
                        <td
                          className="px-3 py-2 font-medium text-slate-900"
                          data-testid={`sf-panel-label-${panel.id}`}
                        >
                          {panel.label}
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-sm flex-shrink-0" 
                              style={{ backgroundColor: sfType?.markerColor || '#39FF14' }}
                            />
                            {sfType?.name || 'N/A'}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          {panel.width_in && panel.height_in ? `${panel.width_in}" × ${panel.height_in}"` : '-'}
                        </td>
                        <td className="px-3 py-2 text-slate-600">{panel.location || '-'}</td>
                        <td className="px-3 py-2 text-center">
                          {panel.qrCodeUrl ? (
                            <button 
                              type="button"
                              className="cursor-pointer hover:opacity-70 transition-opacity inline-block"
                              onClick={() => setQrModal({ open: true, qrToken: panel.qrToken, label: panel.label })}
                              title="Click to enlarge QR code"
                              data-testid={`qr-thumbnail-${panel.id}`}
                            >
                              <img 
                                src={panel.qrCodeUrl} 
                                alt={`QR ${panel.label}`}
                                className="w-8 h-8 mx-auto"
                              />
                            </button>
                          ) : (
                            <span className="text-slate-400 text-xs">-</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center text-slate-600">
                          {isUnused ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300 cursor-help"
                                    data-testid={`sf-panel-quantity-warning-${panel.id}`}
                                  >
                                    <AlertCircle className="h-3 w-3" />
                                    0
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>This schedule is not currently used on any elevation.</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            markerCount
                          )}
                        </td>
                        {canManageProject && (
                          <td className="px-3 py-2 text-right">
                            <div className="flex gap-1 justify-end">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={() => handlePrintSFPanelLabel(panel.id)}
                                title="Print Label"
                                data-testid={`print-sf-panel-${panel.id}`}
                              >
                                <Printer className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={() => handleEditSFPanel(panel)}
                                title="Edit"
                                data-testid={`edit-sf-panel-${panel.id}`}
                              >
                                <Edit className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-red-500 hover:bg-red-600 hover:text-white"
                                onClick={() => handleDeleteSFPanel(panel.id)}
                                title="Delete"
                                data-testid={`delete-sf-panel-${panel.id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
        </>
      )}


      <QRPreviewModal
        open={qrModal.open}
        onClose={() => setQrModal({ open: false, qrToken: null, label: '' })}
        qrToken={qrModal.qrToken}
        productName={qrModal.label}
      />

      {/* Unsaved Changes Confirmation */}
      <AlertDialog open={showDiscardConfirm} onOpenChange={setShowDiscardConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Discard them?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="sf-continue-editing-btn">Continue Editing</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDiscard} className="bg-red-600 hover:bg-red-700" data-testid="sf-discard-changes-btn">
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* SF Panel Excel Import Dialog */}
      <Dialog open={showSFPanelExcelDialog} onOpenChange={setShowSFPanelExcelDialog}>
        <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import Panels from Excel</DialogTitle>
            <DialogDescription>
              AI detected labels and dimensions. Select SF type and panel type for each row.
            </DialogDescription>
          </DialogHeader>
          
          {sfPanelExcelPreview && (
            <div className="mt-4">
              {sfPanelExcelPreview.columnMapping && (
                <div className="mb-4 p-3 bg-blue-50 rounded text-sm">
                  <p className="font-semibold mb-2">AI Detected Columns:</p>
                  <ul className="space-y-1 text-slate-700">
                    <li>• <strong>Labels:</strong> {sfPanelExcelPreview.columnMapping.labelColumn || 'Not found'}</li>
                    <li>• <strong>Dimensions:</strong> {sfPanelExcelPreview.columnMapping.dimensionsColumn || 'Not found'}</li>
                    <li>• <strong>Location:</strong> {sfPanelExcelPreview.columnMapping.locationColumn || 'Not found'}</li>
                  </ul>
                </div>
              )}

              <div className="mb-4 p-3 bg-slate-50 rounded text-sm">
                <p><strong>Total Rows:</strong> {sfPanelExcelPreview.totalRows}</p>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">#</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Label</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Dimensions</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Location</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Panel Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">GL/MT Type</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-200">
                    {sfPanelExcelPreview.rows.map((row, idx) => (
                      <tr key={idx}>
                        <td className="px-4 py-3 text-sm">{row.rowIndex + 1}</td>
                        <td className="px-4 py-3 text-sm font-medium">{row.label}</td>
                        <td className="px-4 py-3 text-sm">
                          {row.width_in && row.height_in ? `${row.width_in}" × ${row.height_in}"` : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm">{row.location}</td>
                        <td className="px-4 py-3 text-sm">
                          <Select
                            value={row.storefrontTypeId || ''}
                            onValueChange={(value) => handleUpdateSFPanelPreviewRow(idx, 'storefrontTypeId', value)}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue placeholder="Select..." />
                            </SelectTrigger>
                            <SelectContent>
                              {sfPanelExcelPreview.sfTypes.map((sf) => (
                                <SelectItem key={sf.id} value={sf.id}>{sf.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <Select
                            value={row.panelType || ''}
                            onValueChange={(value) => handleUpdateSFPanelPreviewRow(idx, 'panelType', value)}
                          >
                            <SelectTrigger className="w-28">
                              <SelectValue placeholder="Type..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="GL">Glass</SelectItem>
                              <SelectItem value="MT">Metal</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {row.panelType === 'GL' && (
                            <Select
                              value={row.glassTypeId || ''}
                              onValueChange={(value) => handleUpdateSFPanelPreviewRow(idx, 'glassTypeId', value)}
                            >
                              <SelectTrigger className="w-28">
                                <SelectValue placeholder="Glass..." />
                              </SelectTrigger>
                              <SelectContent>
                                {sfPanelExcelPreview.glassTypes.map((gl) => (
                                  <SelectItem key={gl.id} value={gl.id}>{gl.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          {row.panelType === 'MT' && (
                            <Select
                              value={row.metalTypeId || ''}
                              onValueChange={(value) => handleUpdateSFPanelPreviewRow(idx, 'metalTypeId', value)}
                            >
                              <SelectTrigger className="w-28">
                                <SelectValue placeholder="Metal..." />
                              </SelectTrigger>
                              <SelectContent>
                                {sfPanelExcelPreview.metalTypes.map((mt) => (
                                  <SelectItem key={mt.id} value={mt.id}>{mt.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex gap-2 mt-4">
                <Button onClick={handleSFPanelBulkImport} className="flex-1">
                  Import Panels
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => {
                    setShowSFPanelExcelDialog(false);
                    setSFPanelExcelPreview(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </TabsContent>
  );
};

export default SFPanelListSection;
